/**
 * Phone sticky add to cart: shown whenever the main buy button is off screen, above it as well as
 * below. The theme's own bar waits until the visitor has scrolled past the button, which on a phone
 * means most visitors never see it.
 *
 * Markup comes from snippets/sticky-atc-reveal.liquid, whose CSS limits all of this to phone widths:
 * on a wider screen the bar stays hidden and the theme's bar is untouched. The bar inside is the
 * theme's <hdt-sticky-btn-atc>, which already syncs the variant with the main picker; this keeps the
 * price and the sale badge on the button in step.
 */

const ACTIVE_BODY_CLASS = 'vw-sticky-active';
const SHOWN_BODY_CLASS = 'vw-sticky-shown';

function formatMoney(cents) {
    const format = window.themeHDN?.settings?.moneyFormat || '€{{amount_with_comma_separator}}';
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return String(cents / 100);

    const group = (value, decimals, thousands, decimal) => {
        const [whole, fraction] = (value / 100).toFixed(decimals).split('.');
        return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
    };

    const formats = {
        amount: () => group(cents, 2, ',', '.'),
        amount_no_decimals: () => group(cents, 0, ',', '.'),
        amount_with_comma_separator: () => group(cents, 2, '.', ','),
        amount_no_decimals_with_comma_separator: () => group(cents, 0, '.', ','),
        amount_with_space_separator: () => group(cents, 2, ' ', ','),
        amount_with_apostrophe_separator: () => group(cents, 2, "'", '.'),
    };

    return format.replace(match[0], (formats[match[1]] || formats.amount)());
}

class StickyAtcReveal extends HTMLElement {
    connectedCallback() {
        this.bar = this.querySelector('hdt-sticky-btn-atc');
        const formId = this.getAttribute('form');
        // The buy buttons rather than the form: the form wraps the whole info column, so it is
        // "in view" long before the button a visitor would press is.
        this.target =
            document.querySelector(`hdt-buy-buttons[form="${formId}"]`) || document.forms[formId] || null;

        if (!this.bar) return;

        // Tells the stylesheet to set the theme's bar aside, on a phone only.
        document.body.classList.add(ACTIVE_BODY_CLASS);

        if (this.target) {
            this.observer = new IntersectionObserver(([entry]) => this.toggle(!entry.isIntersecting));
            this.observer.observe(this.target);
        } else {
            this.toggle(true);
        }

        this.price = this.querySelector('[data-vw-price]');
        this.compare = this.querySelector('[data-vw-compare]');
        this.badge = this.querySelector('[data-vw-badge]');
        this.form = this.querySelector('form');
        this.onVariant = (event) => this.render(event.detail && event.detail.variant);
        if (this.form) this.form.addEventListener('variant:change', this.onVariant);
    }

    disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.form) this.form.removeEventListener('variant:change', this.onVariant);
        document.body.classList.remove(ACTIVE_BODY_CLASS, SHOWN_BODY_CLASS);
    }

    toggle(show) {
        this.bar.classList.toggle('is-shown', show);
        document.body.classList.toggle(SHOWN_BODY_CLASS, show);
    }

    render(variant) {
        if (!variant) return;

        if (this.price) this.price.textContent = formatMoney(variant.price);

        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        if (onSale && !this.compare && this.price) {
            this.compare = document.createElement('s');
            this.compare.className = 'vw-sticky__price-was';
            this.price.after(this.compare);
        }
        if (this.compare) {
            this.compare.hidden = !onSale;
            if (onSale) this.compare.textContent = formatMoney(variant.compare_at_price);
        }

        if (this.badge) {
            this.badge.hidden = !onSale;
            const format = this.badge.dataset.vwBadgeFormat;
            if (onSale && format) {
                const percent = Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
                this.badge.textContent = format.replace('__PCT__', percent);
            }
        }
    }
}

if (!customElements.get('sticky-atc-reveal')) {
    customElements.define('sticky-atc-reveal', StickyAtcReveal);
}

export { StickyAtcReveal };
